import type { FC } from "react";
import styled from "styled-components";
import { theme } from "../../theme";
import Body from "../atoms/typography/Body";
import BodySmall from "../atoms/typography/BodySmall";
import { MailIcon } from "../atoms/icons/MailIcon";
import { PhoneIcon } from "../atoms/icons/PhoneIcon";
import TextTag from "../atoms/TextTag";
import Tooltip from "./Tooltip";
import Small from "../atoms/typography/Small";
import { useTranslation } from "react-i18next";
import type { ClientAmlStatusModel } from "../../models/ClientModel";
import dayjs from "dayjs";

interface Props {
  fullName: string;
  role?: Array<string>;
  email?: string;
  phone?: string;
  clientAmlStatus?: ClientAmlStatusModel;
}

const Container = styled.div`
  background-color: ${theme.colors.bgSubtle};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FlexedDiv = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const ContactCard: FC<Props> = ({
  fullName,
  role,
  email,
  phone,
  clientAmlStatus,
}) => {
  const { t } = useTranslation();

  const getRoleTranslationKey = (role: string) => {
    switch (role) {
      case "BoardMember":
        return "aml.roles.boardMember";
      case "DeputyBoardMember":
        return "aml.roles.deputyBoardMember";
      case "DirectorOfTheBoard":
        return "aml.roles.directorOfTheBoard";
      case "Auditor":
        return "aml.roles.auditor";
      default:
        return role;
    }
  };

  return (
    <Container>
      <FlexedDiv>
        <Body>{fullName}</Body>
        {clientAmlStatus?.isPep && (
          <TextTag tagTitleKey="aml.pep" variant="warning" />
        )}
        {clientAmlStatus?.isSanctioned && (
          <Tooltip
            content={
              <div>
                <Small color={theme.colors.white} fontweight={600}>
                  {t("aml.sanctioned")}
                </Small>
                <Small color={theme.colors.white}>
                  {clientAmlStatus?.typeOfSanction}
                </Small>
                <Small color={theme.colors.white}>
                  {clientAmlStatus?.sanctionDescription}
                </Small>
                <Small color={theme.colors.white}>
                  Uppdaterat:{" "}
                  {dayjs(clientAmlStatus?.updatedTimestamp).format(
                    "YYYY-MM-DD",
                  )}
                </Small>
              </div>
            }
          >
            <TextTag tagTitleKey="aml.sanctioned" variant="warning" />
          </Tooltip>
        )}
      </FlexedDiv>
      {role && (
        <BodySmall>
          {role.map((r) => t(getRoleTranslationKey(r))).join(", ")}
        </BodySmall>
      )}
      {email && (
        <FlexedDiv>
          <MailIcon />
          <BodySmall>{email}</BodySmall>
        </FlexedDiv>
      )}
      {phone && (
        <FlexedDiv>
          <PhoneIcon />
          <BodySmall>{phone}</BodySmall>
        </FlexedDiv>
      )}
    </Container>
  );
};

export default ContactCard;
